import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { CONTACTS, LOGO_URL } from "@/data/content";

const LINKS = [
  { id: "servicos", label: "Serviços" },
  { id: "manutencao", label: "Manutenção" },
  { id: "trabalhos", label: "Trabalhos" },
  { id: "sobre", label: "Sobre" },
  { id: "areas", label: "Áreas" },
  { id: "contactos", label: "Contactos" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate({ pathname: "/", hash: `#${id}` });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,box-shadow] duration-300 ${
          scrolled || open
            ? "border-b border-stone2 bg-ivory/90 shadow-[0_10px_40px_rgba(18,37,26,0.07)] backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 lg:px-10">
          <Link to="/" data-testid="header-logo-link" aria-label="EcoJard, página inicial">
            <img src={LOGO_URL} alt="EcoJard" className="h-14 w-auto sm:h-[68px]" />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegação principal">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-sm font-medium text-ink/80 transition-colors duration-200 hover:text-pine"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 text-sm font-semibold text-pine md:flex">
              <Phone size={16} strokeWidth={2.2} className="shrink-0" />
              <a data-testid="header-phone-link" href={CONTACTS.phoneHref} className="transition-colors hover:text-lime-700">
                {CONTACTS.phoneDisplay}
              </a>
              <span className="text-moss/50">·</span>
              <a data-testid="header-phone2-link" href={CONTACTS.phone2Href} className="transition-colors hover:text-lime-700">
                {CONTACTS.phone2Display}
              </a>
            </div>
            <button
              data-testid="header-quote-button"
              onClick={() => go("orcamento")}
              className="hidden rounded-full bg-pine px-5 py-2.5 text-sm font-semibold text-ivory transition-colors duration-200 hover:bg-pine-hover sm:block"
            >
              Pedir Orçamento
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-stone2 text-pine lg:hidden"
              aria-label={open ? "Fechar menu" : "Abrir menu"}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-30 bg-ivory pt-24 lg:hidden"
          >
            <nav className="flex flex-col gap-2 px-6" aria-label="Menu móvel">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                  onClick={() => go(l.id)}
                  className="border-b border-sand py-4 text-left font-serif text-2xl text-ink"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-quote-button"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => go("orcamento")}
                className="mt-6 rounded-full bg-pine py-4 text-center text-base font-semibold text-ivory"
              >
                Pedir Orçamento Gratuito
              </motion.button>
              <motion.a
                data-testid="mobile-phone-link"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.48 }}
                href={CONTACTS.phoneHref}
                className="mt-3 flex items-center justify-center gap-2 rounded-full border border-pine py-4 text-base font-semibold text-pine"
              >
                <Phone size={18} /> {CONTACTS.phoneDisplay}
              </motion.a>
              <motion.a
                data-testid="mobile-phone2-link"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.54 }}
                href={CONTACTS.phone2Href}
                className="mt-3 flex items-center justify-center gap-2 rounded-full border border-pine py-4 text-base font-semibold text-pine"
              >
                <Phone size={18} /> {CONTACTS.phone2Display}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
